import type { ChannelHealthResponse, SendConfigDto, SystemHealthResponse } from './api.js';

/**
 * R9 — the honest status system, derived in ONE pure place from the two health
 * endpoints. The topbar status pill, the in-frame alert dock, and the health
 * drawer all read these deciders, so the badge can never disagree with the dock
 * (and "Status unknown" surfaces the moment the health feeds fail — alerting
 * can't vanish silently). Replaces the two stand-alone banners.
 */

export type AlertSeverity = 'red' | 'amber';

/** Which health-drawer section an alert's primary action reveals. */
export type DrawerSection = 'channels' | 'worker' | 'cost' | 'analyses' | 'backups';

export interface HealthAlert {
  /** Stable per-condition id — ack/resolve state is keyed off this. */
  id: string;
  severity: AlertSeverity;
  /** Short summary (e.g. "WhatsApp may be disconnected"). */
  title: string;
  /** One-line plain-language explanation + what to do. */
  detail: string;
  /**
   * Changes when the SPECIFIC condition changes (which channels, how many
   * failures). An acknowledged alert re-raises when its signature changes.
   */
  signature: string;
  /** Opens the health drawer; the label says what the operator will see. */
  actionLabel: string;
  section: DrawerSection;
}

export type StatusLevel = 'live' | 'degraded' | 'down' | 'unknown';

export interface StatusVerdict {
  level: StatusLevel;
  label: string;
  /** Breathing dot — true ONLY when fully live AND the push stream is connected. */
  breathing: boolean;
}

function fmtUsd(n: number): string {
  if (n > 0 && n < 0.01) return '<$0.01';
  return `$${n.toFixed(2)}`;
}

export function channelLabel(channel: string): string {
  switch (channel) {
    case 'linkedin':
      return 'LinkedIn';
    case 'whatsapp':
      return 'WhatsApp';
    case 'telegram':
      return 'Telegram';
    case 'email':
      return 'Email';
    default:
      return channel;
  }
}

function listNames(channels: string[]): string {
  const names = channels.map(channelLabel);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Derive the full set of alert conditions. Mirrors EXACTLY what the two deleted
 * banners surfaced (channel disconnected / no-real-time; worker down / cost
 * spike / analysis failures / draft failures / backups local-only). Undefined
 * inputs (not yet loaded) contribute no alerts — "unknown" is handled by
 * deriveStatus from the query error state, not here.
 */
export function deriveAlerts(
  channel: ChannelHealthResponse | undefined,
  system: SystemHealthResponse | undefined,
): HealthAlert[] {
  const alerts: HealthAlert[] = [];

  if (channel) {
    const disconnected = channel.channels.filter((c) => c.connected && !c.healthy);
    if (disconnected.length > 0) {
      const names = disconnected.map((c) => c.channel).sort();
      alerts.push({
        id: 'channel-disconnected',
        severity: 'red',
        title: `${listNames(names)} may be disconnected`,
        detail: "New messages won't arrive until you reconnect the account in Unipile.",
        signature: names.join(','),
        actionLabel: 'View channels',
        section: 'channels',
      });
    }

    const noRealtime = channel.channels.filter(
      (c) =>
        c.connected && c.healthy && (c.webhook_subscribed === false || c.realtime_stale === true),
    );
    if (noRealtime.length > 0) {
      const names = noRealtime.map((c) => c.channel).sort();
      alerts.push({
        id: 'channel-realtime',
        severity: 'amber',
        title: `${listNames(names)} ${names.length > 1 ? 'have' : 'has'} no real-time delivery`,
        detail:
          'Messages are arriving only via the ~15-min safety-net; check or re-create the webhook in Unipile.',
        signature: names.join(','),
        actionLabel: 'View channels',
        section: 'channels',
      });
    }
  }

  if (system) {
    if (!system.worker.alive) {
      const mins = system.worker.minutes_since_sync;
      alerts.push({
        id: 'worker-down',
        severity: 'red',
        title: 'Worker looks down',
        detail: `No background activity${
          mins != null ? ` for ${mins} min` : ''
        }; new messages won't be analysed or auto-answered until it recovers.`,
        signature: 'down',
        actionLabel: 'View worker',
        section: 'worker',
      });
    }

    if (system.cost.spike) {
      const ratio = system.cost.spike_ratio;
      alerts.push({
        id: 'cost-spike',
        severity: 'amber',
        title: 'Analysis cost spike',
        detail: `Today ${fmtUsd(system.cost.today_usd)}${
          ratio != null ? ` (${ratio.toFixed(1)}× the 14-day average)` : ''
        }.`,
        signature: `${Math.round(system.cost.today_usd * 100)}`,
        actionLabel: 'View cost',
        section: 'cost',
      });
    }

    if (system.analyses.failure_alert) {
      const codes = system.analyses.recent_error_codes;
      alerts.push({
        id: 'analysis-failures',
        severity: 'amber',
        title: `${system.analyses.failed_24h} analyses failed`,
        detail: `In the last 24h${codes.length > 0 ? ` (${codes.join(', ')})` : ''}.`,
        signature: `${system.analyses.failed_24h}`,
        actionLabel: 'View failures',
        section: 'analyses',
      });
    }

    if (system.comment_drafts?.failure_alert) {
      const cd = system.comment_drafts;
      alerts.push({
        id: 'draft-failures',
        severity: 'amber',
        title: `${cd.failed_24h}/${cd.total_24h} LinkedIn AI drafts failing`,
        detail: 'In the last 24h — check the engagement / draft path.',
        signature: `${cd.failed_24h}/${cd.total_24h}`,
        actionLabel: 'View failures',
        section: 'analyses',
      });
    }

    // batch-276 — Unipile OUTBOUND auth / ingest safety-net. An auth-dominant
    // (HTTP 401) failure means the API key is rejected, so sends AND enrichment are
    // down (not just the backstop) — red, "check the key". Non-auth backfill
    // failures are an amber degraded-backstop.
    if (system.ingest?.alert) {
      const ing = system.ingest;
      if (ing.reason === 'unipile_auth') {
        alerts.push({
          id: 'unipile-auth',
          severity: 'red',
          title: 'Unipile API key is being rejected',
          detail: `Outbound calls are failing (HTTP 401, ${ing.failed}/${ing.total} recent backfills) — sends and LinkedIn enrichment are down. Check the Unipile API key / subscription.`,
          signature: 'unipile_auth',
          actionLabel: 'View channels',
          section: 'channels',
        });
      } else {
        alerts.push({
          id: 'ingest-backfill',
          severity: 'amber',
          title: 'Ingest safety-net degraded',
          detail: `${ing.failed}/${ing.total} recent backfills are failing — missed-webhook recovery may not run.`,
          signature: `bf:${ing.failed}/${ing.total}`,
          actionLabel: 'View channels',
          section: 'channels',
        });
      }
    }

    if (system.backups && !system.backups.offhost_configured) {
      alerts.push({
        id: 'backups-local',
        severity: 'amber',
        title: 'Backups are local-only',
        detail: "Off-host copies aren't configured; a host loss would lose every backup.",
        signature: 'local-only',
        actionLabel: 'View backups',
        section: 'backups',
      });
    }
  }

  return alerts;
}

/**
 * The 4-state topbar verdict. `errored` (either health query failed) → "Status
 * unknown" so a broken feed is LOUD, never a silent green. Otherwise: any red
 * alert → down; any amber → degraded; else live (breathing iff SSE-connected).
 */
export function deriveStatus(
  alerts: HealthAlert[],
  opts: { errored: boolean; sseConnected: boolean; loaded: boolean },
): StatusVerdict {
  if (opts.errored || !opts.loaded) {
    return { level: 'unknown', label: 'Status unknown', breathing: false };
  }
  if (alerts.some((a) => a.severity === 'red')) {
    return { level: 'down', label: 'Action required', breathing: false };
  }
  if (alerts.length > 0) {
    return { level: 'degraded', label: 'Needs attention', breathing: false };
  }
  return {
    level: 'live',
    label: 'All systems live',
    breathing: opts.sseConnected,
  };
}

/** Per-alert dismissal state (persisted). */
export interface AckEntry {
  /** The signature that was acknowledged — re-raises when the condition changes. */
  ackSignature?: string;
  /** Epoch ms until which the alert is snoozed. */
  snoozeUntil?: number;
}

export type AckState = Record<string, AckEntry>;

/**
 * Is this alert currently suppressed? Acknowledged (same signature) OR snoozed
 * (snooze not yet expired). A signature change OR snooze expiry re-raises it;
 * a condition that fully clears is pruned by the dock hook so it re-raises on
 * return. Pure so the re-raise rules are unit-tested.
 */
export function isAlertHidden(
  alert: HealthAlert,
  entry: AckEntry | undefined,
  now: number,
): boolean {
  if (!entry) return false;
  if (entry.snoozeUntil != null && entry.snoozeUntil > now) return true;
  if (entry.ackSignature != null && entry.ackSignature === alert.signature) return true;
  return false;
}

export interface SendPosture {
  armed: boolean;
  label: string;
}

/** The send-posture pill text from /send-config. */
export function deriveSendPosture(config: SendConfigDto | undefined): SendPosture | null {
  if (!config) return null;
  const armed = config.send_enabled && config.channels.length > 0;
  if (!armed) return { armed: false, label: 'Sending off' };
  const n = config.channels.length;
  return { armed: true, label: `Sends armed · ${n} channel${n === 1 ? '' : 's'}` };
}

export { fmtUsd };
